import { HrefLink as Link } from "@/components/ui/href-link";
import { RequirePermission } from "@/hooks/shared/CheckAuth";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";

import { EmptyState } from "@/components/dashboard/data-table/empty-state";
import { Pagination } from "@/components/dashboard/data-table/pagination";
import { PageHeader } from "@/components/dashboard/page-header";
import { QueryState } from "@/components/dashboard/query-state";
import { StatusPill } from "@/components/dashboard/status-pill";
import { MarkAllRead, OpenNotification } from "@/components/notifications/actions";
import { nav, notificationText, notifications as copy } from "@/content";
import { useDocumentTitle } from "@/hooks/shared/useDocumentTitle";
import { useFmt } from "@/hooks/shared/useFmt";
import { useQueryParams } from "@/hooks/shared/useQueryParams";
import { useSession } from "@/context/UserContext";
import { buildQuery, readPage, readPageSize } from "@/utils/query-params";
import { listNotifications, unreadNotificationCount } from "@/api/system";
import { cn } from "@/utils";

const BASE_PATH = "/dashboard/notifications";

/**
 * The full notification history.
 *
 * Three defects are answered here. The prototype's context fetched `unread=true`
 * only, and marking read deleted from local state — so a read notification became
 * permanently unreachable and there was no history at all. Clicking one removed it
 * from the list without navigating anywhere. And admins received nothing: the bell
 * was supervisor-only by a hardcoded role check, and the `type` CHECK constraint
 * could not express any event beyond the two leave ones, so a geofence breach was
 * unnotifiable without a migration.
 *
 * The text is rendered from the catalogue, keyed by type. The prototype stored a
 * pre-rendered Arabic sentence in a `message` column, which made every
 * notification untranslatable: a user who set their language to English still
 * received Arabic.
 */
export const Route = createFileRoute("/_main/dashboard/notifications")({
  component: () => (
    <RequirePermission action="notifications.read">
      <NotificationsPage />
    </RequirePermission>
  ),
});

function NotificationsPage() {
  useDocumentTitle(copy.title);

  const params = useQueryParams();
  const fmt = useFmt();
  const { user } = useSession();

  const unreadOnly = params.filter === "unread";
  const filters = { unreadOnly, page: readPage(params), pageSize: readPageSize(params) };

  const list = useQuery({
    queryKey: ["notifications", "list", filters],
    queryFn: () => listNotifications(filters),
  });

  /* The same key the shell's bell polls, so marking one read updates both the
     badge and this screen's tab count from a single invalidation. */
  const unreadQuery = useQuery({
    queryKey: ["notifications", "unreadCount"],
    queryFn: unreadNotificationCount,
  });
  const unread = unreadQuery.data ?? 0;
  const result = list.data;

  const tabs = [
    { value: undefined, label: copy.tabAll },
    { value: "unread", label: copy.tabUnread, count: unread },
  ];

  return (
    <div className="mx-auto w-full max-w-shell px-6 py-8">
      <PageHeader
        eyebrow={nav.groups.system}
        title={copy.title}
        lead={copy.lead}
        actions={<MarkAllRead unread={unread} />}
      />

      <div className="mt-6 flex flex-wrap gap-1 border-b border-n3">
        {tabs.map((tab) => {
          const active = (tab.value === "unread") === unreadOnly;
          return (
            <Link
              key={tab.label}
              to={`${BASE_PATH}${buildQuery(params, { filter: tab.value ?? null })}`}
              aria-current={active ? "page" : undefined}
              className={cn(
                "-mb-px inline-flex items-center gap-2 border-b-2 px-3 py-2.5 text-xs font-semibold transition-colors duration-200 ease-brand",
                active ? "border-orange text-n9" : "border-transparent text-n6 hover:text-n8",
              )}
            >
              {tab.label}
              {tab.count !== undefined && tab.count > 0 ? (
                <span className="tabular text-[11px] text-orange-700">
                  {fmt.count(tab.count)}
                </span>
              ) : null}
            </Link>
          );
        })}
      </div>

      <QueryState isPending={list.isPending} isError={list.isError && !list.data} error={list.error}>
      {!result || result.rows.length === 0 ? (
        <div className="mt-8 rounded-lg border border-n3 bg-n0 py-16 shadow-1">
          <EmptyState
            variant={unreadOnly ? "filtered" : "empty"}
            title={unreadOnly ? copy.emptyUnreadTitle : copy.emptyTitle}
            lead={unreadOnly ? copy.emptyUnreadLead : copy.emptyLead}
            actionHref={unreadOnly ? BASE_PATH : undefined}
            actionLabel={unreadOnly ? copy.tabAll : undefined}
          />
        </div>
      ) : (
        <div className="mt-6 overflow-hidden rounded-lg border border-n3 bg-n0 shadow-1">
          <ul className="flex flex-col gap-px bg-n4">
            {result.rows.map((row) => (
              <li
                key={row.id}
                className={cn(
                  "flex flex-wrap items-center gap-3 px-4 py-3.5",
                  /* Unread is a tint, not a bold weight: a list where half the
                     rows shout is a list nobody scans. */
                  row.readAt === null ? "bg-orange/6" : "bg-n0",
                )}
              >
                <span aria-hidden className="size-1.5 shrink-0 rotate-45 bg-orange" />

                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-semibold text-n9">
                    {notificationText[row.type as keyof typeof notificationText] ?? row.type}
                  </span>
                  <span className="tabular mt-0.5 block text-[11px] text-n6">
                    {fmt.dateTime(row.createdAt)}
                  </span>
                </span>

                {row.readAt === null ? (
                  <StatusPill tone="attention">{copy.unreadBadge}</StatusPill>
                ) : null}

                <OpenNotification id={row.id} href={row.deepLink} unread={row.readAt === null} />
              </li>
            ))}
          </ul>

          <Pagination
            page={result.page}
            pageCount={result.pageCount}
            pageSize={result.pageSize}
            total={result.total}
            params={params}
            basePath={BASE_PATH}
            numerals={user.numeralSystem}
          />
        </div>
      )}
      </QueryState>
    </div>
  );
}
